import { useQuery } from '@tanstack/react-query';
import {
  deleteFile,
  fetchDocumentsCollections,
  setDocCollectorFinished,
  trackDocCollectorFileDownload,
} from 'actions';
import { Button } from 'components/ui/Button/Button';
import { ReactComponent as InfoIcon } from 'icons/ico-info-blue.svg';
import { useEffect, useState } from 'react';
import { type ConnectedProps, connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import {
  allFilesAnalyzed,
  allRequiredFilesUploaded,
  documentsCollection,
  getPaymentId,
  hasFilesInDocVerifyProcess,
} from 'selectors';
import { payex } from 'services/payex';
import { createMarkup, useTranslations } from 'utils';
import { DocumentCard } from './DocumentCard';
import { DocumentInVerificationBanner } from './DocumentInVerificationBanner';
import './DocumentCollector.scss';

type PropsFromRedux = ConnectedProps<typeof connector>;

const INTERVAL_MS = 10000;

export const DocumentCollectorComponent = ({
  allFilesAnalyzed,
  allRequiredFilesUploaded,
  deleteFile,
  documentsCollection,
  fetchDocumentsCollections,
  paymentId,
  setDocCollectorFinished,
  trackDocCollectorFileDownload,
  hasFilesInDocVerifyProcess,
}: PropsFromRedux) => {
  const [newFilesAnalyzed, setNewFilesAnalyzed] = useState(false);
  const [docVerifyFinished, setDocVerifyFinished] = useState(false);

  useQuery({
    queryKey: ['documentPolling'],
    queryFn: async () => {
      const response =
        await payex.polling.pollPaymentDocumentsAnalyzed(paymentId);
      setNewFilesAnalyzed(response.documentsAnalyzed);
      if (response.docVerifyFinished !== undefined) {
        setDocVerifyFinished(response.docVerifyFinished);
      }
    },
    refetchInterval: INTERVAL_MS,
    enabled: !allFilesAnalyzed,
  });

  useEffect(() => {
    if (newFilesAnalyzed === true || docVerifyFinished === true) {
      fetchDocumentsCollections();
    }
  }, [fetchDocumentsCollections, newFilesAnalyzed, docVerifyFinished]);

  const i18n = useTranslations();

  const handleContinueClick = () => {
    setDocCollectorFinished(true);
  };

  const renderBanner = !allRequiredFilesUploaded && (
    <div
      className="DocumentCollector-info"
      data-testid="documentCollectorInfoBanner"
    >
      <InfoIcon />
      <span>{i18n.t('documentCollector.instructionsInfo')}</span>
    </div>
  );

  const renderHeader = (
    <div
      className="DocumentCollector-header"
      dangerouslySetInnerHTML={createMarkup(documentsCollection.globalTitle)}
    ></div>
  );

  const renderText = (
    <div
      className="DocumentCollector-text"
      dangerouslySetInnerHTML={createMarkup(
        documentsCollection.globalDescription,
      )}
      data-testid="documentCollectorText"
    ></div>
  );

  const renderCards = documentsCollection?.documents.map(document => (
    <DocumentCard
      key={document.id}
      deleteFile={deleteFile}
      document={document}
      trackFileDownload={trackDocCollectorFileDownload}
    />
  ));

  const renderContinueButton = (
    <div className="DocumentCollector-continueButtonWrapper">
      <Button
        className="DocumentCollector-continueButton Button Button--primary"
        disabled={!allRequiredFilesUploaded}
        onClick={handleContinueClick}
        data-testid="continueButton"
      >
        {i18n.t('documentCollector.continueButton')}
      </Button>
    </div>
  );

  return (
    <div className="DocumentCollector">
      {renderBanner}
      <div className="DocumentCollector-box" data-testid="documentCollector">
        {renderHeader}
        {renderText}
        {renderCards}
        {hasFilesInDocVerifyProcess && <DocumentInVerificationBanner />}
        {renderContinueButton}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  allFilesAnalyzed: allFilesAnalyzed(state),
  allRequiredFilesUploaded: allRequiredFilesUploaded(state),
  documentsCollection: documentsCollection(state),
  paymentId: getPaymentId(state),
  hasFilesInDocVerifyProcess: hasFilesInDocVerifyProcess(state),
});

const mapDispatchToProps = {
  deleteFile,
  fetchDocumentsCollections,
  setDocCollectorFinished,
  trackDocCollectorFileDownload,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export const DocumentCollector = connector(DocumentCollectorComponent);
